import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';

import { mutate } from '../shared/api';

type Locale = 'hr' | 'en';

type SsoResponse = { token: string; url: string; expires_in: number };

/**
 * m.60.4 DevTest — seamless embed odvojene aurodomus/dev-test platforme
 * (programerski test kandidata) UNUTAR BIS-a. Ne otvara novu karticu i ne
 * napusta bis.aurodomus.eu: BIS izda kratkotrajni (60s) potpisani JWT, a
 * dev-test admin se ucita u <iframe> preko BIS-ovog same-origin reverse-proxyja
 * (bis.aurodomus.eu/test/...). Iframe src cilja /test/admin/sso?token=<JWT>, sto
 * dev-test bootstrap zamijeni za svoj AdminToken (sessionStorage unutar
 * iframea) i dalje radi cijeli admin bez izlaska iz BIS-a.
 *
 * Kandidatski javni put (dev.aurodomus.eu/test) je odvojen (drugi server) i ovaj
 * modul ga ne dira — ovdje se prikazuje samo ADMIN sucelje, seamless u BIS-u.
 */
const EMBED_BASE = '/test';

const shell: CSSProperties = { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', minHeight: 560 };
const bar: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #efe6d2', background: '#faf6ec' };
const frameWrap: CSSProperties = { flex: 1, position: 'relative', background: '#fff' };
const iframeStyle: CSSProperties = { width: '100%', height: '100%', border: 0, display: 'block' };
const overlay: CSSProperties = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#665a44', fontSize: '0.95rem' };
const btn: CSSProperties = { background: 'linear-gradient(135deg,#7a5c22,#4a3712)', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' };

export function DevTestModule({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [src, setSrc] = useState<string | null>(null);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const frameRef = useRef<HTMLIFrameElement | null>(null);

    const boot = useCallback(async () => {
        setBusy(true);
        setErr(null);
        setSrc(null);
        try {
            // BIS izda 60s JWT; iframe ga preda dev-test bootstrapu preko
            // same-origin proxyja (ne koristimo res.url jer on cilja vanjski
            // origin — embed mora ostati pod bis.aurodomus.eu).
            const res = await mutate<SsoResponse>('/hr-devtest/sso-token', token, 'POST');
            setSrc(`${EMBED_BASE}/admin/sso?token=${encodeURIComponent(res.token)}`);
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
            setBusy(false);
        }
    }, [token]);

    useEffect(() => {
        void boot();
    }, [boot]);

    return (
        <div style={shell}>
            <div style={bar}>
                <strong style={{ fontSize: '0.95rem', color: '#4a3712' }}>
                    {t('DevTest — programerski test kandidata', 'DevTest — candidate coding test')}
                </strong>
                <span style={{ color: '#8a7c5f', fontSize: '0.8rem' }}>
                    {t('admin sučelje, unutar BIS-a', 'admin console, inside BIS')}
                </span>
                <span style={{ flex: 1 }} />
                <button type="button" style={btn} onClick={() => void boot()}>
                    {t('Osvježi', 'Refresh')}
                </button>
            </div>
            <div style={frameWrap}>
                {src ? (
                    <iframe
                        ref={frameRef}
                        title="DevTest admin"
                        src={src}
                        style={iframeStyle}
                        onLoad={() => setBusy(false)}
                    />
                ) : null}
                {busy || err ? (
                    <div style={overlay}>
                        {err ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ color: '#a12', marginBottom: 12 }}>{err}</div>
                                <button type="button" style={btn} onClick={() => void boot()}>
                                    {t('Pokušaj ponovno', 'Try again')}
                                </button>
                            </div>
                        ) : (
                            <span>{t('Učitavam DevTest…', 'Loading DevTest…')}</span>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
